import {
  Color3,
  DynamicTexture,
  Mesh,
  RawTexture,
  Scene,
  StandardMaterial,
  Texture,
  Constants,
  VertexBuffer,
  VertexData,
} from '@babylonjs/core';
import type { MeshGeometry } from '@roadworld/world';
import { DIAGNOSTIC } from '../engine/Diagnostic.js';
import { PALETTE } from '../engine/palettes.js';
import { TerrainShadingPlugin } from '../engine/TerrainShadingPlugin.js';

export function uploadGeometry(name: string, geom: MeshGeometry, scene: Scene): Mesh {
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = geom.positions;
  vd.indices = geom.indices;
  vd.normals = geom.normals;
  vd.uvs = geom.uvs;
  if (geom.colors) vd.colors = geom.colors;
  vd.applyToMesh(mesh, true);
  mesh.updateVerticesData(VertexBuffer.NormalKind, geom.normals, false, false);
  return mesh;
}

export function makeStandardMat(
  name: string,
  scene: Scene,
  rgb: [number, number, number],
  /** Depth-buffer bias. Negative pulls the surface toward the camera (wins z-fight). */
  zOffset = 0,
): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseColor = new Color3(rgb[0], rgb[1], rgb[2]);
  mat.specularColor = new Color3(0.05, 0.05, 0.05);
  // Phase 1: render both sides so an inverted winding doesn't make geometry invisible.
  mat.backFaceCulling = false;
  if (zOffset !== 0) mat.zOffset = zOffset;
  return mat;
}

/**
 * Shared building-wall material: a procedurally drawn facade with two stacked vertical bands —
 * the TOP half is a typical upper floor (a row of windows), the BOTTOM half is the ground floor
 * (a central door flanked by windows). building.ts maps the ground-floor quad to V [0, 0.5] and
 * every upper-floor quad to V [0.5, 1], so the door appears once at ground level and the window
 * row repeats per storey at a constant scale (no stretching on tall buildings). Tiles
 * horizontally (one tile ≈ 4 m wall, matching FACADE_TILE_WIDTH). Drawn light so the per-building
 * vertex-color wall tint multiplies through it. Cached scene-wide — one texture serves every
 * building.
 */
export function makeBuildingWallMat(scene: Scene): StandardMaterial {
  const cacheKey = '__buildingWallMaterial';
  const sceneAny = scene as Scene & { [cacheKey]?: StandardMaterial };
  const cached = sceneAny[cacheKey];
  if (cached) return cached;

  const W = 256;
  const H = 512;
  const half = H / 2;
  const tex = new DynamicTexture('building-facade-tex', { width: W, height: H }, scene, true);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  // Light base so the vertex-color tint shows through (white * tint = tint).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // Thin dark stroke baked into the texture. Crisp up close; mipmaps blend it away with
  // distance so far buildings read clean instead of muddy. Drawn instead of the old
  // screen-space color-edge outline, which couldn't tell a facade from a road far away.
  const ink = (x: number, y: number, w: number, h: number, lw: number): void => {
    ctx.strokeStyle = '#23262b';
    ctx.lineWidth = lw;
    ctx.strokeRect(x + lw / 2, y + lw / 2, w - lw, h - lw);
  };

  const drawWindow = (x: number, y: number, w: number, h: number): void => {
    ctx.fillStyle = '#cfcabc'; // frame (slightly off-white, darkens the tint a touch)
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#b9c6cd'; // glass (light blue-grey: low contrast so it doesn't mud at range)
    ctx.fillRect(x + w * 0.12, y + h * 0.12, w * 0.76, h * 0.76);
    ctx.fillStyle = '#cfcabc'; // mullions
    ctx.fillRect(x + w * 0.47, y + h * 0.12, w * 0.06, h * 0.76);
    ctx.fillRect(x + w * 0.12, y + h * 0.47, w * 0.76, h * 0.06);
    ink(x, y, w, h, Math.max(2, w * 0.05)); // baked outline around the frame
  };

  // Canvas Y is top-down; with invertY the canvas BOTTOM maps to V=0 (ground). So the bottom
  // canvas half is the ground floor (door), the top canvas half is the repeating upper floor.

  // Upper-floor band: canvas [0, half) → V [0.5, 1]. One centered row of two windows.
  {
    const winW = W * 0.3;
    const winH = half * 0.5;
    const cy = (half - winH) / 2;
    drawWindow(W * 0.1, cy, winW, winH);
    drawWindow(W * 0.6, cy, winW, winH);
  }

  // Ground-floor band: canvas [half, H) → V [0, 0.5]. Central door (flush to the bottom =
  // ground) flanked by two windows.
  {
    const bandTop = half;
    const bandH = half;
    const doorW = W * 0.22;
    const doorH = bandH * 0.7;
    const doorX = (W - doorW) / 2;
    const doorY = H - doorH; // flush with the bottom (ground)
    ctx.fillStyle = '#5a3b24'; // door
    ctx.fillRect(doorX, doorY, doorW, doorH);
    ctx.fillStyle = '#caa46a'; // door knob/handle accent
    ctx.fillRect(doorX + doorW * 0.78, doorY + doorH * 0.5, doorW * 0.08, doorH * 0.08);
    ink(doorX, doorY, doorW, doorH, Math.max(2, doorW * 0.05)); // baked outline around the door
    const gWinW = W * 0.22;
    const gWinH = bandH * 0.4;
    const gWinY = bandTop + bandH * 0.22;
    drawWindow(W * 0.07, gWinY, gWinW, gWinH);
    drawWindow(W * 0.71, gWinY, gWinW, gWinH);
  }

  tex.update(true);
  tex.wrapU = Texture.WRAP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;

  const mat = new StandardMaterial('building-wall-mat-shared', scene);
  mat.diffuseTexture = tex;
  mat.diffuseColor = new Color3(1, 1, 1);
  mat.specularColor = new Color3(0.05, 0.05, 0.05);
  mat.backFaceCulling = false;
  sceneAny[cacheKey] = mat;
  return mat;
}

/**
 * Shared building-roof material: untextured white with low spec; per-building roof color
 * rides in via vertex colors. Cached scene-wide — one material for every roof in the world.
 */
export function makeBuildingRoofMat(scene: Scene): StandardMaterial {
  const cacheKey = '__buildingRoofMaterial';
  const sceneAny = scene as Scene & { [cacheKey]?: StandardMaterial };
  const cached = sceneAny[cacheKey];
  if (cached) return cached;
  const mat = new StandardMaterial('building-roof-mat-shared', scene);
  mat.diffuseColor = new Color3(1, 1, 1);
  mat.specularColor = new Color3(0.04, 0.04, 0.04);
  mat.backFaceCulling = false;
  sceneAny[cacheKey] = mat;
  return mat;
}

/**
 * Terrain material: base StandardMaterial with our TerrainShadingPlugin attached and
 * a per-chunk landcover RGBA texture sampled in the fragment shader.
 */
export function makeTerrainMat(
  name: string,
  scene: Scene,
  cover: { rgba: Uint8Array; resolution: number },
): { material: StandardMaterial; texture: Texture } {
  const mat = new StandardMaterial(name, scene);
  // diffuse color stays white — the plugin overrides baseColor from the cover texture.
  mat.diffuseColor = new Color3(1, 1, 1);
  mat.specularColor = new Color3(0.05, 0.05, 0.05);
  mat.backFaceCulling = false;

  const tex = RawTexture.CreateRGBATexture(
    cover.rgba,
    cover.resolution,
    cover.resolution,
    scene,
    /* generateMipMaps */ false,
    /* invertY */ false,
    Texture.BILINEAR_SAMPLINGMODE,
    Constants.TEXTURETYPE_UNSIGNED_BYTE,
  );
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;

  if (DIAGNOSTIC.ENABLE_TERRAIN_SHADING) {
    // Bind the landcover as the material's standard diffuse texture so baseColor is sampled by
    // the proven StandardMaterial path; the plugin then reads baseColor and overlays noise/rock/
    // snow. (A custom plugin sampler was unreliable here — it read back as white.)
    mat.diffuseTexture = tex;
    const plugin = new TerrainShadingPlugin(mat);
    plugin.enabled = true;
  } else {
    // Diagnostic bypass: terrain plugin off, use a flat palette green so terrain is
    // still visible.
    mat.diffuseColor = PALETTE.terrain.clone();
  }
  // Flag the material so the cel-shading plugin can still compose on top.
  (mat as unknown as { __terrain?: boolean }).__terrain = true;
  return { material: mat, texture: tex };
}
